import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react';
import { type ComponentType, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchInput } from '@/components/search-input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

/** Layers that install a scroll lock around their own content. */
const SCROLL_LOCKED_LAYER_SELECTOR =
  '[data-slot="dialog-content"],[data-slot="sheet-content"]';

/** Keeps the popover off the viewport edges when it is collision-flipped. */
const COLLISION_PADDING = 8;

export type EntityMultiSelectOption = {
  id: string;
  label: string;
  /** Muted trailing text on the option row (e.g. a count). */
  description?: string;
};

/**
 * Tells whether a node sits inside a layer that installs a scroll lock
 * (`Dialog`, `Sheet`).
 *
 * Those layers cancel wheel events targeting anything rendered outside them,
 * and the popover content is portalled to `<body>` — so its option list would
 * be impossible to scroll with the wheel. Making the popover modal in that
 * case gives it a scroll lock of its own, which takes precedence.
 *
 * @param node - the popover trigger, or `null` before it is mounted
 * @returns `true` when the node is inside a dialog or a sheet
 */
export const isInsideScrollLockedLayer = (node: Element | null): boolean =>
  node?.closest(SCROLL_LOCKED_LAYER_SELECTOR) != null;

/**
 * Filters options by a case-insensitive match on their label.
 *
 * @param params.options - all available options
 * @param params.query - the raw search query typed by the user
 * @returns the options whose label contains the trimmed query
 */
export const filterOptionsByQuery = (params: {
  options: EntityMultiSelectOption[];
  query: string;
}): EntityMultiSelectOption[] => {
  const { options, query } = params;
  const normalized = query.trim().toLowerCase();
  if (normalized.length === 0) return options;
  const matches: EntityMultiSelectOption[] = [];
  for (const option of options) {
    if (option.label.toLowerCase().includes(normalized)) matches.push(option);
  }
  return matches;
};

type CommonProps = {
  options: EntityMultiSelectOption[];
  /** Noun shown on the trigger before the count badge (e.g. "Sites"). */
  label: string;
  /** Optional leading icon on the trigger button. */
  icon?: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  /** Show a search input above the option list. */
  searchable?: boolean;
  loading?: boolean;
  disabled?: boolean;
  /** Classes merged on the trigger button. */
  className?: string;
  align?: 'start' | 'end';
};

type ToggleModeProps = CommonProps & {
  /** Default mode: empty selection means "nothing selected". */
  mode?: 'toggle';
  value: string[];
  onValueChange: (ids: string[]) => void;
};

type SplitModeProps = CommonProps & {
  /** Split mode: `undefined` means "all selected implicitly". */
  mode: 'split';
  value: string[] | undefined;
  onValueChange: (ids: string[] | undefined) => void;
};

export type EntityMultiSelectProps = ToggleModeProps | SplitModeProps;

/**
 * Generic popover + checkbox multi-select, ported from the omecare-mockups
 * multi-select pattern (trigger with count badge, toggle-all header, option
 * rows with checkbox and trailing check).
 *
 * The panel is as wide as its trigger, never taller than the space the popover
 * actually has, and becomes modal inside a dialog or a sheet so the option list
 * stays scrollable (see {@link isInsideScrollLockedLayer}).
 *
 * Two selection modes:
 * - `toggle` (default): `value` is always an array; empty = none selected.
 * - `split`: `value === undefined` means "all selected implicitly" — used by
 *   filters that treat "no explicit choice" as "everything".
 *
 * @param props - see {@link EntityMultiSelectProps}
 * @returns the multi-select control
 */
export const EntityMultiSelect = (props: EntityMultiSelectProps) => {
  const {
    options,
    label,
    icon: Icon,
    searchable = false,
    loading = false,
    disabled = false,
    className,
    align = 'start',
  } = props;
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const value = props.value;
  const isSplitMode = props.mode === 'split';
  const isAllImplicit = isSplitMode && value === undefined;
  const selectedSet = useMemo(() => new Set(value ?? []), [value]);
  const selectedCount = isAllImplicit ? options.length : (value?.length ?? 0);
  const allSelected =
    options.length > 0 && (isAllImplicit || selectedCount === options.length);

  const visibleOptions = useMemo(
    () => filterOptionsByQuery({ options, query }),
    [options, query],
  );

  const emit = (next: string[] | undefined) => {
    if (props.mode === 'split') {
      props.onValueChange(next);
      return;
    }
    props.onValueChange(next ?? []);
  };

  const toggleOne = (id: string) => {
    const base = value ?? options.map((option) => option.id);
    const next = base.includes(id)
      ? base.filter((selectedId) => selectedId !== id)
      : [...base, id];
    emit(next);
  };

  const selectAll = () => {
    emit(isSplitMode ? undefined : options.map((option) => option.id));
  };

  const clearAll = () => {
    emit([]);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) setModal(isInsideScrollLockedLayer(triggerRef.current));
    else setQuery('');
    setOpen(nextOpen);
  };

  const focusSearchOnOpen = (event: Event) => {
    if (!searchable) return;
    event.preventDefault();
    searchRef.current?.focus();
  };

  const countLabel = (() => {
    if (selectedCount === 0) return t('entity-multi-select.none');
    if (allSelected)
      return t('entity-multi-select.all', { count: options.length });
    return t('entity-multi-select.some', {
      selected: selectedCount,
      total: options.length,
    });
  })();

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      modal={modal}
    >
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-label={`${label}, ${countLabel}`}
          disabled={disabled || loading}
          className={cn('w-[260px] justify-between font-normal', className)}
        >
          <span className="flex items-center gap-2 truncate">
            {Icon && (
              <Icon
                className="size-4 text-muted-foreground"
                aria-hidden
              />
            )}
            {label}
            <Badge
              variant="secondary"
              className="font-medium tabular-nums"
            >
              {loading ? '…' : countLabel}
            </Badge>
          </span>
          <ChevronsUpDownIcon
            className="size-4 opacity-50"
            aria-hidden
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="flex max-h-[min(var(--radix-popover-content-available-height),20rem)] w-(--radix-popover-trigger-width) min-w-[280px] flex-col p-0"
        align={align}
        collisionPadding={COLLISION_PADDING}
        onOpenAutoFocus={focusSearchOnOpen}
      >
        <div className="flex shrink-0 items-center justify-between gap-2 p-2">
          <span className="min-w-0 truncate font-medium text-muted-foreground text-xs">
            {label}
          </span>
          <div className="flex shrink-0 items-center gap-1">
            {(isSplitMode || !allSelected) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                aria-label={t('entity-multi-select.select-all')}
                onClick={selectAll}
              >
                {t('entity-multi-select.select-all-short')}
              </Button>
            )}
            {selectedCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                aria-label={t('entity-multi-select.deselect-all')}
                onClick={clearAll}
              >
                {t('entity-multi-select.deselect-all-short')}
              </Button>
            )}
          </div>
        </div>
        <Separator className="shrink-0" />
        {searchable && (
          <div className="shrink-0 p-2">
            <SearchInput
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('entity-multi-select.search')}
              aria-label={t('entity-multi-select.search')}
              containerClassName="w-full"
              className="h-8 text-sm"
            />
          </div>
        )}
        <ul className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1">
          {visibleOptions.length === 0 ? (
            <li className="px-2 py-6 text-center text-muted-foreground text-xs">
              {t('entity-multi-select.no-results')}
            </li>
          ) : (
            visibleOptions.map((option) => {
              const checked = isAllImplicit || selectedSet.has(option.id);
              return (
                <li key={option.id}>
                  {/* biome-ignore lint/a11y/noLabelWithoutControl: the Checkbox is the associated control */}
                  <label className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm focus-within:bg-accent hover:bg-accent">
                    <Checkbox
                      checked={checked}
                      aria-label={option.label}
                      onCheckedChange={() => toggleOne(option.id)}
                    />
                    <span className="flex-1 truncate">{option.label}</span>
                    {option.description && (
                      <span className="text-muted-foreground text-xs">
                        {option.description}
                      </span>
                    )}
                    {checked && (
                      <CheckIcon
                        className="size-4 shrink-0 text-primary"
                        aria-hidden
                      />
                    )}
                  </label>
                </li>
              );
            })
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
